import filter from 'lodash/filter.js';

import TemplateTimePeriodRangeData from '@/components/attendance/TemplateTimePeriodRangeData.js';
import Model from '@/models/Model.js';
import moment from '@/plugins/vue-moment.js';

export default class TimePeriodTemplate extends Model {
    static entity = 'time_period_templates';

    static modelName = 'TimePeriodTemplate';

    static fields() {
        return {
            id: this.attr(null),
            course_template_id: this.attr(null),
            section_template_id: this.attr(null),
            title: this.string(''),
            from_day: this.number(null),
            from_time: this.string(''),
            to_day: this.number(null),
            to_time: this.string(''),
            credit_time: this.number(null),
            clock_time: this.number(null),
            break_time: this.number(null),
            considered_present_percent: this.number(0),
            students_can_see_status: this.boolean(false),
            is_for_course_key: this.boolean(true),

            ...super.fields(),

            course_template: this.belongsTo('course_templates', 'course_template_id'),
            section_template: this.belongsTo('section_templates', 'section_template_id'),
            satisfier_templates: this.hasMany('satisfier_templates', 'time_period_template_id'),
            locations: this.belongsToMany(
                'locations',
                'time_period_template_location',
                'location_id',
                'time_period_template_id'
            ),
        };
    }

    get range_data() {
        return new TemplateTimePeriodRangeData(this);
    }

    get pseudo_from() {
        const [hours, minutes, seconds] = this.from_time.split(':');

        return moment()
            .startOf('week')
            .add(this.section_template.section_settings_template.week_number - 1, 'weeks')
            .add(this.from_day - 1, 'days')
            .hour(hours)
            .minute(minutes)
            .second(seconds);
    }

    get pseudo_to() {
        const [hours, minutes, seconds] = this.to_time.split(':');

        return moment()
            .startOf('week')
            .add(this.section_template.section_settings_template.week_number - 1, 'weeks')
            .add(this.to_day - 1, 'days')
            .hour(hours)
            .minute(minutes)
            .second(seconds);
    }

    get range_display() {
        const week = this.section_template.display_week_number;

        return `Week ${week} ${this.pseudo_from.add(1, 'day').format('ddd h:mm a')} - ${this.pseudo_to
            .add(1, 'day')
            .format('ddd h:mm a')}`;
    }

    get scheduled_time() {
        const from = moment(TemplateTimePeriodRangeData.parseTime(this.from_time));
        const to = moment(TemplateTimePeriodRangeData.parseTime(this.to_time)).add(this.to_day - this.from_day, 'days');

        const total_minutes = Math.max(to.diff(from, 'minutes') - this.break_time, 0);
        const hours = Math.floor(total_minutes / 60);

        return {
            total_minutes,
            hours,
            minutes: total_minutes - hours * 60,
            break_minutes: this.break_time,
        };
    }

    isOverlapped(prospectiveTimeRanges) {
        return (
            filter(
                prospectiveTimeRanges.map(([prospectiveFrom, prospectiveTo, timePeriodTemplateId]) => {
                    const from = this.pseudo_from;
                    const to = this.pseudo_to;

                    return (
                        this.id !== timePeriodTemplateId &&
                        ((prospectiveFrom.isSameOrAfter(from) && prospectiveFrom.isBefore(to)) ||
                            (prospectiveTo.isAfter(from) && prospectiveTo.isBefore(to)) ||
                            (prospectiveFrom.isSameOrBefore(from) && prospectiveTo.isSameOrAfter(to)))
                    );
                })
            ).length > 0
        );
    }

    static overlappedTimes(selectedSectionIds, prospectiveTimeRanges, excludeId) {
        return TimePeriodTemplate.query()
            .with('section_template.section_settings_template')
            .where(timePeriodTemplate => selectedSectionIds.includes(timePeriodTemplate.section_template_id))
            .where(timePeriodTemplate => timePeriodTemplate.id !== excludeId)
            .get()
            .filter(timePeriodTemplate => timePeriodTemplate.isOverlapped(prospectiveTimeRanges));
    }

    /**
     *
     * @param field
     * @param tz
     * @returns {Moment}
     */
    timestamp(field, tz) {
        const [hours, minutes, seconds] = this[field].split(':');

        return moment().set({ hours, minutes, seconds });
    }
}
